import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, SubHeader } from 'vetrf-ui';
import { elkEpguApi } from 'api/elkEpgu';
import { ElkEpguRequestInfoDTO } from 'api/elkEpgu/types';
import { InfoTable } from 'components/InfoTable';
import { RemoveModal } from 'components/RemoveModal';
import { NestedBlockWithAccordion } from 'components/FormNestedBlock';
import { OrderingWithSignatureModal } from 'components/OrderingWithSignatureModal/OrderingWithSignatureModal';
import { UserData } from 'components/UserDataContext/types';
import { EntryType } from 'components/registry/types/api';
import { ConfirmModal } from 'components/ConfirmModal';
import { FileType } from 'types/types';
import { SigningPersonDictOption } from 'api/dict/params';
import { ErulRegistrationCodes } from 'api/erul/types';
import { getTooltipForErul } from 'api/erul/utils';
import { erulApi } from 'api/erul';
import { pharmPermissionsApi } from '../../../../api';
import { PermitObjectStatus } from '../../../../../types';
import { FunctionPermissions, PharmPermissionDTO } from '../../../../types/types';
import { InformationTable } from './types';
import {
  epguRegistrationBlockColumn,
  erulBlockColumns,
  erulWithStatusBlockColumns,
  mainBlockColumns,
  permissionApplicationColumns,
  permissionBlockColumns,
  permissionTempBlockColumns,
  rejectBlockColumns,
} from './consts';

type InformationProps = {
  data: PharmPermissionDTO<FileType>;
  registryLink: string;
  removeMethod: (id: string) => void;
  updateData: () => void;
  permissions: FunctionPermissions;
  entryType?: EntryType;
  userData: UserData | null;
};

export const Information = ({
  data,
  removeMethod,
  updateData,
  registryLink,
  permissions,
  entryType,
  userData,
}: InformationProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isRemoveCardModalOpen, setIsRemoveCardModalOpen] = useState(false);
  const [isOrderingModalOpen, setIsOrderingModalOpen] = useState(false);
  const [isElkModalOpen, setIsElkModalOpen] = useState(false);
  const [isErulModalOpen, setIsErulModalOpen] = useState(false);
  const [elkRequestInfo, setElkRequestInfo] = useState<ElkEpguRequestInfoDTO>();
  const [error, setError] = useState('');

  const openOrderingModal = () => {
    setIsOrderingModalOpen(true);
  };

  const closeOrderingModal = () => {
    setIsOrderingModalOpen(false);
  };

  const openRemoveModal = () => {
    setIsRemoveCardModalOpen(true);
  };

  const closeRemoveModal = () => {
    setIsRemoveCardModalOpen(false);
  };

  const openErulModal = () => {
    setIsErulModalOpen(true);
  };

  const closeErulModal = () => {
    setIsErulModalOpen(false);
  };

  const onRemoveCard = async () => {
    await removeMethod(data.id!);
    closeRemoveModal();
    navigate(registryLink);
  };

  const createOrderingWithSignature = (signingPerson: SigningPersonDictOption) => {
    closeOrderingModal();
    const sendData = {
      ...signingPerson,
      id: data.id!,
      signed: false,
    };
    pharmPermissionsApi.getPdf(sendData);
  };

  const createOrdering = () => {
    const sendData = {
      id: data.id!,
      signed: true,
    };
    pharmPermissionsApi.getPdf(sendData);
  };

  const filterColumns = (columns: InformationTable) =>
    columns.filter((item) => {
      if (item.isPermitted) {
        return data?.permitted;
      }
      if (item.isRegistred) {
        return !!userData;
      }
      return (
        (item.mode.includes(EntryType.registry) && entryType === EntryType.registry) ||
        (item.mode.includes(EntryType.requests) && entryType === EntryType.requests)
      );
    });

  const openElkEpguModal = () => {
    setIsElkModalOpen(true);
  };

  const closeElkEpguModal = () => {
    setIsElkModalOpen(false);
  };

  const registerToEpgu = async () => {
    closeElkEpguModal();
    setError('');
    await elkEpguApi
      .registerPharmPerm(data.id!, elkRequestInfo?.requestType?.id)
      .then(() => updateData())
      .catch((err) => setError(err.response.data.message));
  };

  const getErulFile = async () => {
    setError('');
    erulApi
      .downloadPharmPermissionFileForErul(data.id!)
      .then()
      .catch(async (err) => {
        const errorData = JSON.parse(await err.response.data.text());
        setError(errorData.message);
      });
  };

  const sendToErul = async () => {
    closeErulModal();
    setError('');
    await erulApi
      .sendPharmPermissionToErul(data.id!)
      .then(updateData)
      .catch((err) => setError(err.response.data.message));
  };

  useEffect(() => {
    if (permissions.elk) {
      elkEpguApi.getPharmPermRequestInfo(data.id!).then(setElkRequestInfo);
    }
  }, [permissions.elk]);

  const mainBlockData = [
    {
      item: {
        recipientName: data?.recipient?.name,
        recipientId: data?.recipient?.id,
        recipientNotLinkedWithCerberus: data.recipientNotLinkedWithCerberus,
        recipientIncorrectCerberusLink: data.recipientIncorrectCerberusLink,
        inn: data?.recipient?.businessEntity?.inn,
        ogrn: data?.recipient?.businessEntity?.ogrn,
        regNumber: data?.regNumber,
        registryProductName: data?.registryProduct?.name || data?.registryProductName,
        registryProductInterName: data?.registryProductInterName,
        activeSubstanceName: data?.activeSubstance?.name || data?.activeSubstanceName,
        releaseForm: data?.releaseForm,
        releaseVolume: data?.releaseVolume,
        measuringUnit: data?.measuringUnit?.shortName,
        packType: data?.packType,
        dosage: data?.dosage,
        packCount: data?.packCount,
        packDoses: data?.packDoses,
        packSerialNumber: data?.packSerialNumber,
        producerList: data?.producerList,
        authorizedPerson: data?.authorizedPerson,
        qualityDocumentInformation: {
          qualityDocNumber: data?.qualityDocumentInformation?.qualityDocNumber,
          qualityDocDate: data?.qualityDocumentInformation?.qualityDocDate,
          qualityDocFile: data?.qualityDocumentInformation?.qualityDocFile,
        },
        confirmDocumentInformation: {
          confirmDocNumber: data?.confirmDocumentInformation?.confirmDocNumber,
          confirmDocDate: data?.confirmDocumentInformation?.confirmDocDate,
          confirmDocFile: data?.confirmDocumentInformation?.confirmDocFile,
        },
        testReportInformation: {
          testReportNumber: data?.testReportInformation?.testReportNumber,
          testReportDate: data?.testReportInformation?.testReportDate,
          testReportOrganization: data?.testReportInformation?.testReportOrganization,
          testReportFile: data?.testReportInformation?.testReportFile,
        },
        requestDate: data?.requestDate,
        status: data?.status?.name,
        requestNumber: data?.requestNumber,
        requestRegNumber: data?.requestRegNumber,
        requestRegDate: data?.requestRegDate,
        requestMethod: data?.requestMethod,
        epguRegistration: data?.epguRegistrationList?.length
          ? data?.epguRegistrationList[data.epguRegistrationList.length - 1]
          : null,
      },
    },
  ];

  return (
    <>
      {error && (
        <Alert closable messageType="danger">
          {error}
        </Alert>
      )}
      <SubHeader
        header="Информация"
        icon="info"
        iconColor="orange"
        iconSize={120}
        level={4}
        toolbar={
          <div className="info-toolbar">
            {((permissions.patentee &&
              (data.status?.code === PermitObjectStatus.NEW ||
                data.status?.code === PermitObjectStatus.REVISION)) ||
              (permissions.rshsn &&
                (data.status?.code === PermitObjectStatus.RECEIVED ||
                  data.status?.code === PermitObjectStatus.REJECTED ||
                  data.status?.code === PermitObjectStatus.REVIEW ||
                  data.status?.code === PermitObjectStatus.CHANGING ||
                  data.status?.code === PermitObjectStatus.EXPIRED))) &&
              data.erulRegistration?.status.code !== ErulRegistrationCodes.ERUL_NEW &&
              data.erulRegistration?.status.code !== ErulRegistrationCodes.ERUL_IN_PROGRESS && (
                <Button
                  icon="pencil"
                  iconColor="green"
                  iconSize={150}
                  onClick={() => {
                    navigate(`/registry/pharmPermissions/${entryType}/${id}/edit`);
                  }}
                  onlyIcon
                  tooltip="Редактировать"
                />
              )}
            {permissions.rshsn && entryType === EntryType.registry && (
              <div className="card-btn-group">
                <Button
                  icon=" orderingWithSignature"
                  iconColor="orange"
                  iconSize={150}
                  onClick={createOrdering}
                  onlyIcon
                  tooltip="Формирование выписки с ЭП"
                />
                <Button
                  icon=" ordering"
                  iconColor="orange"
                  iconSize={150}
                  onClick={openOrderingModal}
                  onlyIcon
                  tooltip="Формирование выписки без ЭП"
                />
              </div>
            )}
            {permissions.erul &&
              entryType === EntryType.requests &&
              (data.status?.code === PermitObjectStatus.REVIEW ||
                data.status?.code === PermitObjectStatus.RECEIVED) && (
                <div className="card-btn-group">
                  <Button
                    icon="file-code-o"
                    iconColor="orange"
                    iconSize={150}
                    onClick={getErulFile}
                    onlyIcon
                    tooltip="Выгрузить данные для регистрации в ЕРУЛ вручную"
                  />
                  <Button
                    disabled={
                      data.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_NEW ||
                      data.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_IN_PROGRESS
                    }
                    icon="send-o"
                    iconColor="orange"
                    iconSize={150}
                    onClick={openErulModal}
                    onlyIcon
                    tooltip={getTooltipForErul(data.erulRegistration)}
                  />
                </div>
              )}
            {permissions.elk && (
              <div className="card-btn-group">
                <Button
                  disabled={!elkRequestInfo?.operationApplicable}
                  icon="id-card"
                  iconColor="blue"
                  iconSize={150}
                  onClick={openElkEpguModal}
                  onlyIcon
                  tooltip="Передача в ЕЛК ЕПГУ статуса заявления о регистрации объекта"
                />
              </div>
            )}

            {((permissions.patentee &&
              data.status?.code === PermitObjectStatus.NEW &&
              userData?.currentBusinessEntity?.id === data.recipient.id) ||
              (permissions.rshsn && data.status?.code === PermitObjectStatus.RECEIVED)) && (
              <Button
                icon="trash"
                iconColor="red"
                iconSize={150}
                onClick={openRemoveModal}
                onlyIcon
                tooltip="Удалить"
              />
            )}
          </div>
        }
      />
      <div>
        {entryType !== EntryType.registry && (
          <NestedBlockWithAccordion
            title="Заявка на получение разрешения на ввод в гражданский оборот иммунобиологических ЛП"
            compact
          >
            <InfoTable itemList={mainBlockData} rows={filterColumns(mainBlockColumns)} />
          </NestedBlockWithAccordion>
        )}
        {entryType === EntryType.registry && (
          <NestedBlockWithAccordion title="Разрешение на ввод в гражданский оборот" compact>
            <InfoTable
              itemList={[
                {
                  item: {
                    ...data?.permissionInformation,
                    packSerialNumber: data?.packSerialNumber,
                    erulManualChanged: data?.erulManualChanged,
                    erulRegistration: data?.erulRegistration,
                  },
                },
              ]}
              rows={filterColumns([
                ...(permissions.erul ? erulWithStatusBlockColumns : erulBlockColumns),
                ...permissionBlockColumns,
              ])}
            />
          </NestedBlockWithAccordion>
        )}
        {entryType === EntryType.requests &&
          permissions.erul &&
          data?.permissionInformation?.permissionNumber &&
          data?.permissionInformation?.permissionDate && (
            <NestedBlockWithAccordion title="Разрешение на ввод в гражданский оборот" compact>
              <InfoTable
                itemList={[
                  {
                    item: {
                      ...data?.permissionInformation,
                      erulManualChanged: data?.erulManualChanged,
                      erulRegistration: data?.erulRegistration,
                    },
                  },
                ]}
                rows={filterColumns(erulWithStatusBlockColumns)}
              />
            </NestedBlockWithAccordion>
          )}
        {permissions.rshsn && (
          <NestedBlockWithAccordion title="Заявление на получение разрешения" compact>
            <InfoTable
              itemList={mainBlockData}
              rows={filterColumns(
                permissions.elk
                  ? [...permissionApplicationColumns, epguRegistrationBlockColumn]
                  : permissionApplicationColumns
              )}
            />
          </NestedBlockWithAccordion>
        )}
        {permissions.rshsn && (
          <NestedBlockWithAccordion
            title="Временное разрешение на ввод в гражданский оборот"
            compact
          >
            <InfoTable
              itemList={[{ item: { ...data?.permissionTempInformation } }]}
              rows={filterColumns(permissionTempBlockColumns)}
            />
          </NestedBlockWithAccordion>
        )}
        {entryType === EntryType.registry && (
          <NestedBlockWithAccordion
            title="Сведения разрешения на ввод в гражданский оборот иммунобиологических ЛП"
            compact
          >
            <InfoTable itemList={mainBlockData} rows={filterColumns(mainBlockColumns)} />
          </NestedBlockWithAccordion>
        )}
        {data.status?.code === PermitObjectStatus.REJECTED && (
          <NestedBlockWithAccordion title="Заявка отклонена" compact>
            <InfoTable
              itemList={[{ item: { ...data?.rejectInformation } }]}
              rows={filterColumns(rejectBlockColumns)}
            />
          </NestedBlockWithAccordion>
        )}
        {isOrderingModalOpen && (
          <OrderingWithSignatureModal
            onClose={closeOrderingModal}
            onSubmit={createOrderingWithSignature}
          />
        )}

        {isRemoveCardModalOpen && (
          <RemoveModal
            onClose={closeRemoveModal}
            onRemove={onRemoveCard}
            modalContent="Вы уверены, что хотите удалить запись?"
          />
        )}

        {isErulModalOpen && (
          <ConfirmModal
            onClose={closeErulModal}
            onConfirm={sendToErul}
            modalContent="Вы уверены, что хотите отправить запрос на регистрацию в ЕРУЛ?"
          />
        )}

        {isElkModalOpen && (
          <ConfirmModal
            onClose={closeElkEpguModal}
            onConfirm={registerToEpgu}
            modalContent={`Вы уверены, что хотите выполнить по заявлению ${elkRequestInfo?.requestNumber} от ${elkRequestInfo?.requestDate} запрос "${elkRequestInfo?.requestType?.name}"?`}
          />
        )}
      </div>
    </>
  );
};
